import { useLocation, useNavigate } from "react-router-dom";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
} from "../../StyledComponents/Container";
import { Backdrop, Box, CircularProgress, InputAdornment, Typography } from "@mui/material";
import { StyledInputLabel } from "../../StyledComponents/InputLabel";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import { useEffect, useState } from "react";
import * as constants from "../../../../constants";
import { getInvoiceDetails } from "../../services/RCBOInvoices/RCBOInvoiceServices";
import basestyle from "../../../CSS/Base.module.css";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../../store/store";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid2";
import CustomTextField from "../../../../shared/CustomTextFeild";
import { notifyUser } from "../../Utils/notifyUser";

interface Column {
  id: string;
  label: string;
  minWidth: any;
  align?: "center" | "left" | "right";
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

const formatCurrencyPositive = (value: number): string => {
  const absValue = Math.abs(value); // Remove negative sign
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(absValue);
};

const InvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const userInfo = useSelector((state: RootState) => state.auth);
  const nsInvoiceNumber = location.state?.netsuiteInvoiceNumber;
  const amountDue = location.state?.outstandingBalance;
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNumber: "",
    customerNo: "",
    customerName: "",
    postingDescription: "",
    postingDate: "",
    documentDate: null,
    documentNo: "",
    status: "",
    amountIncludingTax: 0,
    subTotal: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filteredData, setFilteredData] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyrowsPerPage, setHistoryRowsPerPage] = useState(5);
  const [historyFilteredData, setHistoryFilteredData] = useState([]);
  const [blackdropFlag, setBlackdropFlag] = useState(false)
  const [openLineItems, setOpenLineItems] = useState(true);
  const [openHistory, setOpenHistory] = useState(true);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleHistoryPageChange = (newPage: number) => {
    setHistoryPage(newPage);
  };

  const handleHistoryRowsPerPageChange = (newRowsPerPage: number) => {
    setHistoryRowsPerPage(newRowsPerPage);
    setHistoryPage(0);
  };

  async function getInvoiceDetailsBynsNumber() {
    setBlackdropFlag(true);
    try {
      const responseList = await getInvoiceDetails(
        nsInvoiceNumber,
        userInfo?.defaultParticipantId,
        userInfo.clientId
      );

      if (responseList.status === constants.STATUS_CODES.SUCCESS) {
        const { nsItemsVOList, nsCustomerPaymentsList, ...rest } =
          responseList.data.data;
        setFilteredData(nsItemsVOList);
        setHistoryFilteredData(nsCustomerPaymentsList);
        setInvoiceDetails(rest);
      } else {
        setFilteredData([]);
        setHistoryFilteredData([]);
        notifyUser(responseList.response.data.message, 'error', translate('netsuiteInvoice'));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setBlackdropFlag(false);
    }
  }

  const paginatedInvoiceItems = filteredData
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((item: any) => ({
      ...item,
      amount: formatCurrencyPositive(item.amount),
      rate: item.rate != null ? formatCurrencyPositive(item.rate) : "", // Optional
    }));

  const paginatedInvoiceHistory =
    historyFilteredData != null &&
      historyFilteredData != undefined &&
      historyFilteredData.length > 0
      ? historyFilteredData
        .slice(
          historyPage * historyrowsPerPage,
          historyPage * historyrowsPerPage + historyrowsPerPage
        )
        .map((item: any) => ({
          ...item,
          date: formatDate(item.date),
          amount: formatCurrencyPositive(item.amount),
        }))
      : [];

  useEffect(() => {
    if (nsInvoiceNumber) {
      getInvoiceDetailsBynsNumber();
    }
  }, [nsInvoiceNumber]);

  const InvoiceLineItemsColumnNames: Column[] = [
    { id: "item", label: translate("item"), minWidth: "200px" },
    { id: "units", label: translate("units"), minWidth: "100px" },
    {
      id: "quantity",
      label: translate("quantity"),
      minWidth: "100px",
      align: "center",
    },
    { id: "rate", label: translate("rate"), minWidth: "100px", align: "right" },
    { id: "description", label: translate("description"), minWidth: "250px" },
    {
      id: "amount",
      label: translate("amount"),
      minWidth: "100px",
      align: "right",
    },
  ];

  const InvoiceHistoryColumnNames: Column[] = [
    {
      id: "date",
      label: translate("transactionDate"),
      minWidth: "100px",
      align: "center",
    },
    { id: "type", label: translate("type"), minWidth: "100px" },
    {
      id: "amount",
      label: translate("amount"),
      minWidth: "100px",
      align: "right",
    },
  ];

  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>
            {translate("netsuiteInvoice")} : {invoiceDetails?.invoiceNumber}
          </SectionTitle> 
            <Box sx={{ width: "100%" }}>
              <StyledTwoChildContainer sx={{ padding: "40px" }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("invoiceNo")}
                      value={invoiceDetails?.invoiceNumber}
                      readOnly
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("status")}
                      value={invoiceDetails?.status}
                      readOnly
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 4 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("customerName")}
                      value={invoiceDetails?.customerName}
                      readOnly
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("totalAmount")}
                      value={invoiceDetails?.amountIncludingTax}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      readOnly
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 4 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("postingDescription")}
                      value={invoiceDetails?.postingDescription}
                      readOnly
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("amountDue")}
                      value={amountDue.replace("$", "")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      readOnly
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, sm: 0, lg: 4 }}
                    sx={{ display: { lg: "block", sm: "none", xs: "block" } }}
                  ></Grid>
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <CustomTextField
                      labelText={translate("postingDate")}
                      value={formatDate(invoiceDetails?.postingDate)}
                      readOnly
                    />
                  </Grid>
                </Grid>
              </StyledTwoChildContainer>
              <Accordion
                sx={{ my: 1 }}
                expanded={openLineItems}
                onChange={() => setOpenLineItems(!openLineItems)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={openLineItems ? basestyle.accordionHeader : ""}
                  style={!openLineItems ? { background: "#E5F2F4" } : {}}
                >
                  <StyledInputLabel sx={{ fontSize: "20px" }}>
                    {translate("invoiceLineItems")}
                  </StyledInputLabel>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "white" }}>
                  <ReusableTable
                    columns={InvoiceLineItemsColumnNames}
                    rows={paginatedInvoiceItems}
                    count={filteredData.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion
                expanded={openHistory}
                onChange={() => setOpenHistory(!openHistory)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={openHistory ? basestyle.accordionHeader : ""}
                  style={!openHistory ? { background: "#E5F2F4" } : {}}
                >
                  <StyledInputLabel sx={{ fontSize: "20px" }}>
                    {translate("invoiceHistory")}
                  </StyledInputLabel>
                </AccordionSummary>
                <AccordionDetails sx={{ backgroundColor: "white" }}>
                  <ReusableTable
                    columns={InvoiceHistoryColumnNames}
                    rows={paginatedInvoiceHistory}
                    count={historyFilteredData ? historyFilteredData.length : 0}
                    page={historyPage}
                    rowsPerPage={historyrowsPerPage}
                    onPageChange={handleHistoryPageChange}
                    onRowsPerPageChange={handleHistoryRowsPerPageChange}
                  />
                </AccordionDetails>
              </Accordion>
              <div className={basestyle.buttoncontainer}>
                <ContainedButton
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/rcboInvoiceSummaryListing")}
                >
                  {translate("back")}
                </ContainedButton>
              </div>
            </Box> 
          <Backdrop
            sx={(theme) => ({
              color: "#fff",
              zIndex: theme.zIndex.drawer + 1,
              display: "flex",
              flexDirection: "column",
            })}
            open={blackdropFlag}
          >
            <CircularProgress color="inherit" />
            <Typography color="white">Please wait...</Typography>
          </Backdrop>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default InvoiceDetails;
